// SPDX-License-Identifier: AGPL-3.0-or-later
import { useState } from "react";
import { LOCALES, type Locale } from "@almanac/i18n";
import { HouseholdSetup } from "../components/HouseholdSetup.js";
import { api } from "../lib/api.js";
import { useApp } from "../lib/app.js";
import { useI18n } from "../lib/i18n.js";
import { PALETTES, colorsFor, complementary, setPrefs, usePrefs, type Colors, type Mode, type PaletteId } from "../lib/prefs.js";
import { useStore } from "../lib/store.js";

const MODES: Mode[] = ["light", "grey", "dark", "system"];
const PALETTE_IDS = [...(Object.keys(PALETTES) as PaletteId[]), "custom"] as PaletteId[];
const LANGUAGE_NAMES: Record<Locale, string> = { "en-CA": "English (Canada)", "fr-CA": "Français (Canada)" };

export function Settings() {
  const { store, signOut } = useApp();
  const state = useStore(store);
  const prefs = usePrefs();
  const { t } = useI18n();
  const [confirmDelete, setConfirmDelete] = useState("");
  const colors = colorsFor(prefs);

  const setCustom = (key: keyof Colors, value: string) => {
    const custom = { ...prefs.custom, [key]: value };
    // Picking a budget colour pairs it with its complement for "actual".
    if (key === "budget") custom.actual = complementary(value);
    if (key === "positive") custom.negative = complementary(value);
    setPrefs({ palette: "custom", custom });
  };

  const exportData = () => {
    const { accounts, months, plan, learned, events, profile, custom } = state;
    const data = { format: "almanac-export", version: 2, accounts, months, plan, learned, events, profile, customCategories: custom };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `almanac-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", alignItems: "start" }}>
      <section className="card" aria-labelledby="household-title">
        <h2 id="household-title">{t("settings.household")}</h2>
        <p className="hint" style={{ marginBottom: 12 }}>
          {t("settings.householdHint")}
        </p>
        <HouseholdSetup firstRun={false} />
      </section>
      <section className="card" aria-labelledby="appearance-title">
        <h2 id="appearance-title">{t("settings.appearance")}</h2>
        <fieldset>
          <legend>{t("settings.mode")}</legend>
          <div className="choices">
            {MODES.map((m) => (
              <label key={m}>
                <input type="radio" name="mode" checked={prefs.mode === m} onChange={() => setPrefs({ mode: m })} />
                {t(`mode.${m}`)}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>{t("settings.palette")}</legend>
          <div className="stack" style={{ gap: 8 }}>
            {PALETTE_IDS.map((id) => {
              const c = id === "custom" ? prefs.custom : PALETTES[id as Exclude<PaletteId, "custom">];
              return (
                <label key={id} className="row" style={{ fontWeight: 450 }}>
                  <input type="radio" name="palette" checked={prefs.palette === id} onChange={() => setPrefs({ palette: id })} />
                  <span className="palette-swatches" aria-hidden="true">
                    {[c.positive, c.negative, c.budget, c.actual].map((color, i) => (
                      <i key={i} style={{ background: color }} />
                    ))}
                  </span>
                  {id === "custom" ? t("palette.custom") : t(`palette.${id as Exclude<PaletteId, "custom">}`)}
                </label>
              );
            })}
          </div>
        </fieldset>
        {prefs.palette === "custom" && (
          <fieldset>
            <legend>{t("palette.custom")}</legend>
            <p className="hint">{t("settings.customHint")}</p>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {(["positive", "negative", "budget", "actual"] as const).map((key) => (
                <div className="field" key={key}>
                  <label htmlFor={`color-${key}`}>{t(`settings.color.${key}`)}</label>
                  <input id={`color-${key}`} type="color" value={colors[key]} onChange={(e) => setCustom(key, e.target.value)} />
                </div>
              ))}
            </div>
          </fieldset>
        )}
        <div className="field">
          <label htmlFor="language">{t("settings.language")}</label>
          <select
            id="language"
            value={prefs.locale}
            onChange={(e) => {
              const locale = e.target.value as Locale;
              setPrefs({ locale });
              if (!store.backend.demo) void api.updateMe({ locale }).catch(() => undefined);
            }}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l} lang={l}>
                {LANGUAGE_NAMES[l]}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="card" aria-labelledby="data-title">
        <h2 id="data-title">{t("settings.data")}</h2>
        <div className="stack">
          <button type="button" onClick={exportData}>
            {t("settings.export")}
          </button>
          {store.backend.demo ? (
            <button type="button" onClick={() => location.reload()}>
              {t("settings.demoReset")}
            </button>
          ) : (
            <>
              <button type="button" onClick={signOut}>
                {t("auth.signOut")}
              </button>
              <form
                className="stack"
                onSubmit={async (e) => {
                  e.preventDefault();
                  await api.deleteMe();
                  location.assign("/");
                }}
              >
                <div className="field">
                  <label htmlFor="confirm-delete">{t("settings.deleteLabel")}</label>
                  <input id="confirm-delete" type="text" value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)} autoComplete="off" />
                  <p className="hint">{t("settings.deleteHint")}</p>
                </div>
                <button type="submit" className="danger" disabled={confirmDelete !== "DELETE"}>
                  {t("settings.delete")}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      <section className="card" aria-labelledby="about-title">
        <h2 id="about-title">{t("settings.about")}</h2>
        <p>{t("app.tagline")}</p>
        <p>{t("settings.version", { version: "0.1.0" })}</p>
        <p>{t("settings.license")}</p>
      </section>
    </div>
  );
}
